'use client'

import { useEffect, useRef } from 'react'
import { useApolloClient } from '@material-bank/graphql-client'
import { useAuthProvider } from '@material-bank/mtag-authentication'

// Entitlement-dependent queries (Find Similar, Pairs With) are filtered per
// user, but Apollo's cache is keyed only by query variables. Login and logout
// happen inside a dialog with no navigation, so without this the previous
// identity's results stay mounted — an anonymous full-catalog list would survive
// a restricted user signing in. Clearing the store refetches every active query
// under the new identity, and drops the cached constructorUserContext the
// browser's Constructor link filters by.
export function AuthApolloBridge() {
  const { user, status, sessionRenewed } = useAuthProvider()
  const client = useApolloClient()
  const previousUserIdRef = useRef<string | null>(null)
  const hasSettledRef = useRef(false)

  useEffect(() => {
    // AuthProvider starts at 'loading' and resolves the session on mount, so the
    // first identity to arrive is the page's starting identity, not a change of
    // identity. Adopt it without clearing the store — otherwise every logged-in
    // page load would refetch each active query a second time, doubling the
    // GraphQL load and flashing the results.
    if (status === 'loading') {
      return
    }

    const userId = user?.id ?? null

    if (!hasSettledRef.current) {
      hasSettledRef.current = true
      previousUserIdRef.current = userId

      // Unless /auth/me had to renew the session to answer: the page's first
      // queries went out before that, as a guest, and must refetch as the user.
      if (sessionRenewed) {
        client.resetStore().catch(() => {
          // Failed refetches surface their own error states.
        })
      }

      return
    }

    if (userId === previousUserIdRef.current) {
      return
    }

    previousUserIdRef.current = userId
    client.resetStore().catch(() => {
      // resetStore rejects when a refetch fails; those queries surface their
      // own error states.
    })
  }, [user, status, sessionRenewed, client])

  return null
}
